import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Download, X, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { datevAPI } from '../lib/api';

function defaultRange() {
  const today = new Date();
  const firstOfThis = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastOfPrev = new Date(firstOfThis.getTime() - 86400000);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(firstOfPrev), to: iso(lastOfPrev) };
}

function filenameFromHeader(disposition, fallback) {
  if (!disposition) return fallback;
  const m = disposition.match(/filename="?([^";]+)"?/i);
  return m ? m[1] : fallback;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function entityLink(issue, slug) {
  if (issue.entity_type === 'buchung') {
    return <Link to={`/mandanten/${slug}/journal`}>Journal öffnen</Link>;
  }
  if (issue.entity_type === 'beleg' && issue.entity_id) {
    return <Link to={`/mandanten/${slug}/belege/${issue.entity_id}`}>Beleg #{issue.entity_id}</Link>;
  }
  if (issue.entity_type === 'ausgangsrechnung') {
    return <Link to={`/mandanten/${slug}/ausgang`}>Ausgang öffnen</Link>;
  }
  if (issue.entity_type === 'kunde') {
    return <Link to={`/mandanten/${slug}/kunden`}>Kunden öffnen</Link>;
  }
  if (issue.entity_type === 'lieferant') {
    return <Link to={`/mandanten/${slug}/lieferanten`}>Lieferanten öffnen</Link>;
  }
  if (issue.entity_type === 'mandant') {
    return <Link to={`/mandanten`}>Mandant-Einstellungen</Link>;
  }
  return null;
}

function IssueRow({ issue, slug }) {
  const isError = issue.severity === 'error';
  const color = isError ? '#991b1b' : '#854d0e';
  const bg = isError ? '#fef2f2' : '#fffbeb';
  return (
    <div style={{
      background: bg, borderLeft: `3px solid ${color}`,
      padding: 10, marginBottom: 6, borderRadius: 4, fontSize: 13,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
        <div>
          <b style={{ color }}>{isError ? 'FEHLER' : 'Hinweis'}</b>{' '}
          <span className="muted small">[{issue.code}]</span>
          <div style={{ marginTop: 2 }}>{issue.message}</div>
          {issue.hint && <div className="muted small" style={{ marginTop: 2 }}>{issue.hint}</div>}
        </div>
        <div style={{ flexShrink: 0 }}>
          {entityLink(issue, slug)}
        </div>
      </div>
    </div>
  );
}

export default function DatevExportModal({ open, onClose, mandant, onExported }) {
  const init = defaultRange();
  const slug = mandant?.slug;
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [includePdfs, setIncludePdfs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);

  const configMissing = mandant && (
    mandant.datev_berater_nr == null ||
    mandant.datev_mandant_nr == null ||
    mandant.datev_wj_beginn == null
  );

  const reset = () => {
    setBusy(false);
    setReport(null);
    setAcknowledgedWarnings(false);
    onClose();
  };

  // Bei Datumsänderung Validierung verwerfen — alte Ergebnisse passen sonst nicht mehr
  const setFromAndReset = (v) => { setFrom(v); setReport(null); setAcknowledgedWarnings(false); };
  const setToAndReset = (v) => { setTo(v); setReport(null); setAcknowledgedWarnings(false); };

  const runValidation = async () => {
    if (from > to) {
      toast.error("'Von' muss vor 'Bis' liegen");
      return;
    }
    if (configMissing) {
      toast.error('DATEV-Stammdaten am Mandanten fehlen');
      return;
    }
    setBusy(true);
    setReport(null);
    try {
      const r = await datevAPI.validate(slug, { from, to });
      setReport(r.data);
      if (r.data.ok && r.data.warnings.length === 0) {
        toast.success('Validierung sauber — bereit zum Export');
      } else if (r.data.ok) {
        toast(`${r.data.warnings.length} Hinweis(e) — Export möglich`, { icon: '⚠️' });
      } else {
        toast.error(`${r.data.errors.length} Fehler — bitte fixen`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validierung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const runExport = async (dryRun) => {
    if (configMissing) {
      toast.error('DATEV-Stammdaten am Mandanten fehlen');
      return;
    }
    if (!report) {
      toast.error('Erst validieren');
      return;
    }
    if (!report.ok) {
      toast.error('Fehler im Report müssen erst behoben werden');
      return;
    }
    if (!dryRun && report.warnings.length > 0 && !acknowledgedWarnings) {
      toast.error('Hinweise bitte explizit bestätigen');
      return;
    }
    setBusy(true);
    try {
      const r = await datevAPI.fullExport(slug, {
        from, to, dry_run: dryRun, include_pdfs: includePdfs,
      });
      const count = parseInt(r.headers['x-fibu-buchung-count'] || '0', 10);
      const fallback = `DATEV_${slug}_${from}_${to}${dryRun ? '_DRYRUN' : '_FINAL'}.zip`;
      const filename = filenameFromHeader(r.headers['content-disposition'], fallback);
      triggerDownload(r.data, filename);
      if (dryRun) {
        toast.success(`Vorschau: ${count} Buchung(en) im ZIP`);
      } else {
        toast.success(`${count} Buchung(en) endgültig exportiert`);
        if (onExported) await onExported();
        reset();
      }
    } catch (err) {
      let msg = 'Export fehlgeschlagen';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          msg = JSON.parse(text).detail || msg;
        } else {
          msg = err.response?.data?.detail || msg;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const canExportFinal = report && report.ok && (report.warnings.length === 0 || acknowledgedWarnings);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={reset} className="modal-overlay">
        <Transition.Child
          as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to"
        >
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="modal-positioner">
          <Transition.Child
            as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to"
          >
            <Dialog.Panel className="modal-panel" style={{ width: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header">
                <Dialog.Title className="modal-title">DATEV-Voll-Export für Stotax</Dialog.Title>
                <button className="btn-text" onClick={reset} aria-label="Schließen">
                  <X size={16} />
                </button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1 }}>
                {configMissing && (
                  <div className="card" style={{
                    background: '#fffbeb', borderLeft: '3px solid #d97706',
                    padding: 10, marginTop: 12, fontSize: 13,
                  }}>
                    <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} />{' '}
                    <b>DATEV-Stammdaten unvollständig.</b> Bitte erst Berater-Nr,
                    Mandant-Nr und WJ-Beginn in den Mandant-Einstellungen pflegen.
                  </div>
                )}

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 16 }}>
                  <label>
                    Von (Belegdatum)
                    <input type="date" value={from} onChange={(e) => setFromAndReset(e.target.value)} />
                  </label>
                  <label>
                    Bis (Belegdatum)
                    <input type="date" value={to} onChange={(e) => setToAndReset(e.target.value)} />
                  </label>
                  <label style={{ gridColumn: '1 / span 2', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={includePdfs}
                      onChange={(e) => setIncludePdfs(e.target.checked)}
                      style={{ width: 'auto' }} />
                    Beleg-PDFs ins ZIP bündeln (volle GoBD-Übergabe)
                  </label>
                </div>

                <div style={{ marginTop: 16 }}>
                  <button className="btn-secondary" onClick={runValidation} disabled={busy || configMissing}>
                    {busy && !report ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />}{' '}
                    Validieren
                  </button>
                </div>

                {report && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                      <div>
                        <div className="muted small">Buchungen</div>
                        <div style={{ fontWeight: 600 }}>{report.counts.buchungen}</div>
                      </div>
                      <div>
                        <div className="muted small">Debitoren</div>
                        <div style={{ fontWeight: 600 }}>{report.counts.debitoren}</div>
                      </div>
                      <div>
                        <div className="muted small">Kreditoren</div>
                        <div style={{ fontWeight: 600 }}>{report.counts.kreditoren}</div>
                      </div>
                      <div style={{ marginLeft: 'auto' }}>
                        {report.ok ? (
                          <span style={{ color: '#166534', fontWeight: 600 }}>
                            <CheckCircle size={14} style={{ verticalAlign: 'middle' }} /> 0 Fehler
                          </span>
                        ) : (
                          <span style={{ color: '#991b1b', fontWeight: 600 }}>
                            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {report.errors.length} Fehler
                          </span>
                        )}
                        {report.warnings.length > 0 && (
                          <span style={{ color: '#854d0e', marginLeft: 8 }}>
                            · {report.warnings.length} Hinweis(e)
                          </span>
                        )}
                      </div>
                    </div>

                    {report.errors.map((i, k) => <IssueRow key={`e${k}`} issue={i} slug={slug} />)}
                    {report.warnings.map((i, k) => <IssueRow key={`w${k}`} issue={i} slug={slug} />)}

                    {report.ok && report.warnings.length > 0 && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 13 }}>
                        <input type="checkbox"
                          checked={acknowledgedWarnings}
                          onChange={(e) => setAcknowledgedWarnings(e.target.checked)}
                          style={{ width: 'auto', marginTop: 3 }} />
                        <span>
                          Ich habe die {report.warnings.length} Hinweis(e) zur Kenntnis genommen
                          und möchte trotzdem endgültig exportieren
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>

              <div className="actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginTop: 12 }}>
                <button className="btn-secondary" onClick={() => runExport(true)} disabled={busy || !report || !report.ok}>
                  <Download size={14} /> Vorschau (dry-run)
                </button>
                <button className="btn-primary" onClick={() => runExport(false)} disabled={busy || !canExportFinal}>
                  <Download size={14} /> Endgültig exportieren
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
